import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Breadcrums from "../components/breadcrums";
import PageTitle from "../components/pageTitle";
import { nanoid } from "nanoid";
import moment from "moment";

// Define types
interface Employee {
    uuid: string;
    name: string;
    pid: string;
    p_id: string; // Added p_id property
    p_name: string;
    entity: string; // Added entity property
    start_dt?: string; // Add start date (optional)
    end_dt?: string;   // Add end date (optional)
    active?: number;   // Add active property (optional)
}

type TileProps = {
    date: Date;
    view: "month" | "year" | "decade" | "century";
};

interface PunchedHourEntry {
    date: string; // ISO format "YYYY-MM-DD"
    hours: string;
    employeeId: string | null;
    _d?: Date;
    hrs?: number;
}

interface InvoiceEntry {
    uuid: string;
    pid: string;
    invoice_type: "monthly" | "weekly";
    invoice_month?: number;
    invoice_year?: number;
    invoice_week?: number;
    generated_date: string;
}



const breadcrumLinks = [
    { name: "Timesheet", href: "/timesheet", current: false },
    { name: "Master Timesheet", href: "/timesheet/editor/xyz", current: true },
];

export default function TimesheetEditor() {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
    const [selectedPid, setSelectedPid] = useState<string | null>(null);
    const [activeStatus, setActiveStatus] = useState<"all" | "active" | "inactive">("all"); // New state for employee status
    
    const [date, setDate] = useState<Date>(new Date());
    const [currentMonth, setCurrentMonth] = useState<{ month: number; year: number }>({
        month: new Date().getMonth() + 1, // JavaScript months are 0-indexed
        year: new Date().getFullYear(),
    });
    const [punchedHours, setPunchedHours] = useState<PunchedHourEntry[]>([
        // { date: "2025-01-01", hours: "8", employeeId: selectedEmployee || null }
    ]);
    const [refreshKey, setRefreshKey] = useState(0); // State to force re-render
    const [tileRefreshTrigger, setTileRefreshTrigger] = useState(0); // State to trigger tile content refresh
    const [employeeStatusFilter, setEmployeeStatusFilter] = useState<"all" | "active" | "inactive">("all");
    const [focusedDate, setFocusedDate] = useState<Date>(new Date());
    const [timezone, setTimezone] = useState<"IST" | "US">("IST"); // Default to IST
    const [invoices, setInvoices] = useState<InvoiceEntry[]>([]);

    // Auto-detect timezone on mount
    useEffect(() => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz === "Asia/Kolkata") {
            setTimezone("IST");
        } else {
            setTimezone("US");
        }
    }, []);

    // Helper function to normalize date format as UTC "YYYY-MM-DD"
    const normalizeDate = (date: Date): string => {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            console.error("Invalid date provided to normalizeDate:", date);
            return "";
        }
        return moment(date).format("YYYY-MM-DD"); // Use local time, not UTC
    };

    // Helper to convert any input to UTC "YYYY-MM-DD"
    const toYYYYMMDD = (input: Date | string | number): string => {
        const m = moment.utc(input);
        if (!m.isValid()) {
            console.error("Invalid date:", input);
            return "";
        }
        return m.format("YYYY-MM-DD");
    };

    // Helper function to determine if an employee is active
    const isEmployeeActive = (employee: Employee): boolean => {
        const today = new Date();
        const startDate = employee.start_dt ? new Date(employee.start_dt) : null;
        const endDate = employee.end_dt ? new Date(employee.end_dt) : null;

        return (
            (!startDate || startDate <= today) &&
            (!endDate || endDate >= today)
        );
    };

    // Fetch employees on mount
    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        query: `SELECT c.uuid, c.active, c.name AS name, c.entity, GROUP_CONCAT(DISTINCT p.pid SEPARATOR ', ') AS p_id, GROUP_CONCAT(DISTINCT pm.name SEPARATOR ', ') AS p_name, MIN(p.start_dt) AS start_dt, MAX(p.end_dt) AS end_dt FROM consultant AS c LEFT JOIN project AS p ON c.uuid = p.uuid AND p.active = true LEFT JOIN project_master AS pm  ON p.pid = pm.pid GROUP BY c.uuid, c.name, c.entity ORDER BY c.name, c.uuid `,
                        params: ['consultant', 'project', 'project_master'],
                    }),
                });
                if (!response.ok) throw new Error("Failed to fetch employees");
                const data: Employee[] = await response.json();
                console.log("Fetched Employees:", data); // Log the fetched employees
                setEmployees(data);
            } catch (error) {
                console.error("Error fetching employees:", error);
            }
        };
        fetchEmployees();
    }, []);

    // Fetch invoices when employee or month changes
    useEffect(() => {
        const fetchInvoices = async () => {
            if (!selectedEmployee || !selectedPid) return;
            try {
                console.log("Fetching invoices for:", { selectedEmployee, selectedPid, currentMonth });
                
                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        query: `SELECT DISTINCT f.uuid, f.pid, 
                                p.billing_period as invoice_type,
                                MONTH(f._d) as invoice_month, 
                                YEAR(f._d) as invoice_year, 
                                WEEK(f._d, 4) as invoice_week, 
                                MIN(f._d) as generated_date,
                                f.invoice_no
                                FROM finance f
                                JOIN project p ON f.pid = p.pid
                                WHERE f.uuid = '${selectedEmployee}' 
                                AND f.pid IN (${selectedPid})
                                AND f.invoice_no IS NOT NULL 
                                AND f.invoice_no != '' 
                                AND f.invoice_no != '0' 
                                AND f.invoice_no != 'null'
                                AND TRIM(f.invoice_no) != ''
                                AND YEAR(f._d) = ${currentMonth.year}
                                GROUP BY f.uuid, f.pid, MONTH(f._d), YEAR(f._d), WEEK(f._d, 4), p.billing_period, f.invoice_no`,
                        params: ['finance'],
                    }),
                });
                
                console.log("Invoice fetch response status:", response.status);
                
                if (!response.ok) {
                    console.warn("Invoice fetch failed, trying alternative queries");
                    
                    // First try: Check for invoice_no in finance table (comprehensive check)
                    const financeInvoiceResponse = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            query: `SELECT uuid, pid, 'monthly' as invoice_type, ${currentMonth.month} as invoice_month, ${currentMonth.year} as invoice_year, 0 as invoice_week, MIN(_d) as generated_date, invoice_no,
                                           COUNT(*) as record_count
                                    FROM finance 
                                    WHERE uuid = '${selectedEmployee}' 
                                    AND pid IN (${selectedPid})
                                    AND (
                                        (invoice_no IS NOT NULL AND invoice_no != '' AND invoice_no != '0' AND invoice_no != 'null')
                                        OR 
                                        (TRIM(COALESCE(invoice_no, '')) != '')
                                    )
                                    AND MONTH(_d) = ${currentMonth.month} 
                                    AND YEAR(_d) = ${currentMonth.year}
                                    GROUP BY uuid, pid, invoice_no
                                    HAVING COUNT(*) > 0`,
                            params: ['finance'],
                        }),
                    });
                    
                    console.log("Checking finance table for invoice_no...");
                    if (financeInvoiceResponse.ok) {
                        const invoiceData = await financeInvoiceResponse.json();
                        console.log("Invoice check response:", invoiceData);
                        if (invoiceData && invoiceData.length > 0) {
                            console.log("Found invoices by invoice_no:", invoiceData);
                            setInvoices(invoiceData);
                            return;
                        } else {
                            console.log("No invoice numbers found in finance table");
                        }
                    } else {
                        console.error("Finance invoice query failed:", financeInvoiceResponse.status);
                    }
                    
                    // Second try: Check for billing/payment status
                    const alternativeResponse = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            query: `SELECT uuid, pid, 'monthly' as invoice_type, ${currentMonth.month} as invoice_month, ${currentMonth.year} as invoice_year, 0 as invoice_week, MIN(_d) as generated_date 
                                    FROM finance 
                                    WHERE uuid = '${selectedEmployee}' 
                                    AND pid IN (${selectedPid})
                                    AND (paid > 0 OR payment > 0)
                                    AND MONTH(_d) = ${currentMonth.month} 
                                    AND YEAR(_d) = ${currentMonth.year}
                                    GROUP BY uuid, pid
                                    HAVING COUNT(*) > 0`,
                            params: ['finance'],
                        }),
                    });
                    
                    if (alternativeResponse.ok) {
                        const data: InvoiceEntry[] = await alternativeResponse.json();
                        console.log("Fetched invoices (alternative):", data);
                        setInvoices(data);
                        return;
                    }
                    
                    throw new Error("Failed to fetch invoices from both sources");
                }
                
                const data: InvoiceEntry[] = await response.json();
                console.log("Fetched invoices:", data);
                console.log("Setting invoices state:", data);
                setInvoices(data);
            } catch (error) {
                console.error("Error fetching invoices:", error);
                setInvoices([]);
            }
        };
        fetchInvoices();
    }, [selectedEmployee, selectedPid, currentMonth]);

    // Fetch punched hours when employee or month changes
    useEffect(() => {
        const fetchPunchedHours = async () => {
            if (!selectedEmployee) return;
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/dynamic`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        // query: "SELECT  hrs,DAY(_d) as day,WEEK(_d,4) as week, _d , uuid, pid, hrs, verified from finance where  " + " MONTH(_d) =  " + currentMonth.month + " AND YEAR(_d) = " + currentMonth.year + " and uuid ='" + selectedEmployee + "'  ",
query: "SELECT f.hrs, DAY(f._d) AS day, WEEK(f._d, 4) AS week, f._d, f.uuid, f.pid, f.verified, COUNT(*) AS entry_count, MIN(p.start_dt) AS start_dt, MAX(p.end_dt) AS end_dt " + "FROM finance f " + "JOIN project p ON f.pid = p.pid " + "WHERE MONTH(f._d) = " + currentMonth.month + " AND YEAR(f._d) = " + currentMonth.year + " AND f.uuid = '" + selectedEmployee + "' " + "GROUP BY f._d, f.hrs, f.uuid, f.pid, f.verified " + "ORDER BY f._d, f.hrs",
                        //+ " and pid = '" + 1 + "'"
                        params: ["finance"],
                     }),
                });

                console.log(response);

                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

                if (response.ok) {
                    const data: PunchedHourEntry[] = await response.json();
                    console.info(data);

                    if (!Array.isArray(data)) throw new Error("Invalid data format received from API");

                    setPunchedHours(
                        data.map((entry) => {
                            // const parsedDate = entry?._d ? new Date(entry._d) : null;
                            // const correctedDate = parsedDate; // Adjust for timezone offset
                            //+ parsedDate.getTimezoneOffset() * 60000
                            // correctedDate ? toYYYYMMDD(correctedDate) : "", // Use corrected date
                            return {
                                ...entry,
                                date: entry?._d ? toYYYYMMDD(entry._d) : "",
                                employeeId: selectedEmployee,
                                hours: entry?.hrs?.toString() || "0",
                            };
                        })
                    );

                    console.info("Punched hours fetched successfully:", punchedHours);
                }
            } catch (error) {
                console.error("Error fetching punched hours:", error);
                setPunchedHours([]);
            }
        };
        fetchPunchedHours();
    }, [selectedEmployee, currentMonth]);

    const handleDateClick = (date: Date, moveNext = false) => {
        const formattedDate = normalizeDate(date); // Format date as "YYYY-MM-DD"
        
        // Check if the selected date is within the employee's project duration
        if (!selectedEmployee) {
            alert("Please select an employee first.");
            return;
        }

        // Check if invoice is already generated for this date
        const dateWeek = moment(date).week();
        const dateMonth = date.getMonth() + 1;
        const dateYear = date.getFullYear();

        console.log("Checking invoices for date:", { date: formattedDate, dateWeek, dateMonth, dateYear, invoices });
        
        // Check if any invoice exists that would prevent editing this date
        const blockingInvoice = invoices.find(invoice => {
            const invoiceType = invoice.invoice_type?.toLowerCase();
            if (invoiceType === 'monthly') {
                // For monthly billing, block entire month if invoice exists
                return invoice.invoice_month === dateMonth && invoice.invoice_year === dateYear;
            } else if (invoiceType === 'weekly') {
                // For weekly billing, block only the specific week if invoice exists
                return invoice.invoice_week === dateWeek && invoice.invoice_year === dateYear;
            }
            return false;
        });

        if (blockingInvoice) {
            const period = blockingInvoice.invoice_type === 'monthly' 
                ? `month ${blockingInvoice.invoice_month}/${blockingInvoice.invoice_year}`
                : `week ${blockingInvoice.invoice_week} of ${blockingInvoice.invoice_year}`;
            alert(`Cannot edit timesheet - ${blockingInvoice.invoice_type} invoice has already been generated for ${period}.`);
            return;
        }

        const selectedEmployeeData = employees.find((employee) => employee.uuid === selectedEmployee);
        if (!selectedEmployeeData) {
            alert("Selected employee data not found.");
            return;
        }

        // Check if the date is within the project duration
        if (selectedEmployeeData.start_dt && selectedEmployeeData.end_dt) {
            const startDate = new Date(selectedEmployeeData.start_dt);
            const endDate = new Date(selectedEmployeeData.end_dt);
            
            if (date < startDate || date > endDate) {
                alert(`Data entry is restricted to project duration: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`);
                return;
            }
        }

        const newHours = prompt(`Enter punched hours for ${formattedDate}`, "8");

        if (!newHours || isNaN(Number(newHours))) {
            alert("Invalid hours entered. Please try again.");
            return;
        }

        console.warn("Selected PID:", selectedPid);
        console.warn("Selected Employee:", selectedEmployee);
        console.warn("Selected Date:", formattedDate);
        console.warn("New Hours:", newHours);
        console.warn("Punched Hours:", punchedHours);
        console.warn("Selected Employee:", employees.find((employee) => employee.uuid === selectedEmployee));

        console.warn("Selected Employees:", employees);

        console.warn("Selected Employee Project Name:", employees.find((employee) => employee.uuid === selectedEmployee)?.p_name);
        // console.warn("Selected Employee PID:", employees.find((employee) => employee.uuid === selectedEmployee)?.pid);
        // console.warn("Selected Employee Start Date:", employees.find((employee) => employee.uuid === selectedEmployee)?.start_dt);
        // console.warn("Selected Employee End Date:", employees.find((employee) => employee.uuid === selectedEmployee)?.end_dt);
        console.warn("Selected Employee Active:", employees.find((employee) => employee.uuid === selectedEmployee)?.active);
        console.warn("Selected Employee Entity:", employees.find((employee) => employee.uuid === selectedEmployee)?.entity);
        console.warn("Selected Employee Project Name:", employees.find((employee) => employee.uuid === selectedEmployee)?.p_name);
        

        if (!selectedPid) {
            alert("No project is mapped to the selected employee. Please select another employee.");
            return;
        }

        const updatePunchedHours = async () => {
            const payload = {
                uuid: selectedEmployee,
                pid: selectedPid,
                _d: formattedDate,
                paid: 0,
                hrs: newHours,
                _ts_bill: normalizeDate(new Date()),
                payment: 0,
                verified: true,
            };

            console.log("Payload being sent to API:", payload);

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/finance`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("API Error:", response.status, errorText);
                    alert(`Failed to update punched hours: ${errorText}`);
                    return;
                }

                // Update punched hours locally
                const existingEntryIndex = punchedHours.findIndex((entry) => entry.date === formattedDate);
                if (existingEntryIndex !== -1) {
                    setPunchedHours((prev) =>
                        prev.map((entry, idx) =>
                            idx === existingEntryIndex
                                ? { ...entry, hours: newHours }
                                : entry
                        )
                    );
                } else {
                    setPunchedHours((prev) => [
                        ...prev,
                        { date: formattedDate, hours: newHours, employeeId: selectedEmployee },
                    ]);
                }

                setTileRefreshTrigger((prev) => prev + 1); // Trigger tile content refresh

                // Move to next cell if requested
                if (moveNext) {
                    const nextDate = new Date(date);
                    nextDate.setDate(nextDate.getDate() + 1);
                    setFocusedDate(nextDate);
                    setDate(nextDate);
                }
            } catch (error) {
                console.error("Error updating punched hours:", error);
                alert("An error occurred while updating punched hours. Please try again.");
            }
        };

        updatePunchedHours();
        setDate(date);
    };

    // Keyboard navigation handler
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "SELECT") return;
            let newDate = new Date(focusedDate);
            switch (e.key) {
                case "ArrowLeft":
                    newDate.setDate(newDate.getDate() - 1);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "ArrowRight":
                    newDate.setDate(newDate.getDate() + 1);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "ArrowUp":
                    newDate.setDate(newDate.getDate() - 7);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "ArrowDown":
                    newDate.setDate(newDate.getDate() + 7);
                    setFocusedDate(newDate);
                    setDate(newDate);
                    break;
                case "Enter":
                    handleDateClick(focusedDate, true); // Move to next cell after entry
                    break;
                default:
                    break;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [focusedDate, handleDateClick]);

    // Highlight focused cell
    const renderTileContent = React.useCallback(
        ({ date, view }: TileProps) => {
            if (view === "month") {
                // Format as local date string to match calendar tile and backend
                const formattedTileDate = moment(date).format("YYYY-MM-DD");

                const punchedHour = punchedHours.find(
                    (entry) => entry.date === formattedTileDate
                );
                const isFocused =
                    date.toDateString() === focusedDate.toDateString();
                
                // Check if the date is disabled
                const isDisabled = renderTileDisabled({ date, view });
                
                return (
                    <div
                        style={{
                            fontSize: "12px",
                            textAlign: "center",
                            color: isDisabled ? "#d1d5db" : "gray",
                            background: isFocused ? "#e0e7ff" : undefined,
                            borderRadius: isFocused ? "50%" : undefined,
                            opacity: isDisabled ? 0.5 : 1,
                        }}
                    >
                        {punchedHour && punchedHour.hours && punchedHour.hours !== "0"
                            ? `${punchedHour.hours}h`
                            : ""}
                    </div>
                );
            }
            return null;
        },
        [punchedHours, tileRefreshTrigger, focusedDate, timezone, selectedEmployee, employees, invoices]
    );

    const renderTileDisabled = ({ date, view }: TileProps): boolean => {
        if (view !== "month" || !selectedEmployee) return false;

        // Check if invoice has been generated for this date based on billing period
        const dateWeek = moment(date).week();
        const dateMonth = date.getMonth() + 1;
        const dateYear = date.getFullYear();

        console.log("Checking tile disabled for date:", { 
            date: moment(date).format('YYYY-MM-DD'), 
            dateWeek, 
            dateMonth, 
            dateYear, 
            invoicesCount: invoices.length,
            invoices: invoices.map(inv => ({
                ...inv,
                invoice_month_type: typeof inv.invoice_month,
                invoice_year_type: typeof inv.invoice_year,
                invoice_week_type: typeof inv.invoice_week
            }))
        });

        // Check if any invoice exists that would disable this date
        const isInvoiced = invoices.some(invoice => {
            console.log("Comparing invoice:", invoice, "with date:", { dateWeek, dateMonth, dateYear });
            
            // Handle case-insensitive comparison for invoice_type
            const invoiceType = invoice.invoice_type?.toLowerCase();
            
            if (invoiceType === 'monthly') {
                // For monthly billing, disable entire month if invoice exists
                const match = Number(invoice.invoice_month) === Number(dateMonth) && Number(invoice.invoice_year) === Number(dateYear);
                console.log("Monthly invoice match:", match, "invoice month:", invoice.invoice_month, "(", typeof invoice.invoice_month, ") vs date month:", dateMonth, "(", typeof dateMonth, ")");
                return match;
            } else if (invoiceType === 'weekly') {
                // For weekly billing, disable only the specific week if invoice exists
                const match = Number(invoice.invoice_week) === Number(dateWeek) && Number(invoice.invoice_year) === Number(dateYear);
                console.log("Weekly invoice match:", match, "invoice week:", invoice.invoice_week, "date week:", dateWeek);
                return match;
            }
            console.log("No match for invoice type:", invoiceType);
            return false;
        });

        console.log("Final isInvoiced result:", isInvoiced);
        if (isInvoiced) return true;

        const selectedEmployeeData = employees.find((employee) => employee.uuid === selectedEmployee);
        if (!selectedEmployeeData) return false;

        // If no start_dt or end_dt, don't disable any dates
        if (!selectedEmployeeData.start_dt || !selectedEmployeeData.end_dt) return false;

        const startDate = new Date(selectedEmployeeData.start_dt);
        const endDate = new Date(selectedEmployeeData.end_dt);

        // Normalize dates to avoid timezone issues
        const dateToCheck = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const normalizedStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const normalizedEndDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        // Disable the tile if the date is outside the project duration
        return dateToCheck < normalizedStartDate || dateToCheck > normalizedEndDate;
    };

    const handleNavigation = (e) => {
        // console.log(e);
        // console.log(e.action);
        // console.log(e.activeStartDate);

        // Selected User
        console.log(selectedEmployee);

        //  Update View - Month-Year reference
        setCurrentMonth({
            month: new Date(e.activeStartDate).getMonth() + 1, // JavaScript months are 0-indexed
            year: new Date(e.activeStartDate).getFullYear(),
        });

        // Load Finance APIs for selected range and UUID
        setDate(new Date(e.activeStartDate)); // Set the date to the first day of the month
    };

    const totalHours = React.useMemo(() => {
        // Deduplicate by date, keeping the last entry for each date
        const map = new Map<string, PunchedHourEntry>();
        punchedHours
            .filter(entry => entry.employeeId === selectedEmployee)
            .forEach(entry => map.set(entry.date, entry));
        return Array.from(map.values()).reduce((total, entry) => {
            const h = parseFloat(entry.hours || "0");
            return !isNaN(h) ? total + h : total;
        }, 0);
    }, [punchedHours, selectedEmployee]);

    return (
        <main>
            <Breadcrums breadcrumLinks={breadcrumLinks} module={"Dashboard"} />
            <PageTitle title="Master Timesheet" preSummary="Operational Master Timesheet" summary="Administrator Console"></PageTitle>

            <section className="px-4 py-6 sm:px-6 lg:px-8">
                {/* Dropdown to filter employees by status */}
                <label>Filter Employees:</label>
                <select
                    onChange={(e) => setActiveStatus(e.target.value as "all" | "active" | "inactive")}
                    value={activeStatus}
                    className="block px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm w-96 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                    <option value="all">All Employees</option>
                    <option value="active">Active Employees</option>
                    <option value="inactive">Inactive Employees</option>
                </select>

                <label className="mt-4 block">Select Employee:</label>
                <select
                    onChange={(e) => {
                        const selectedUuid = e.target.value;
                        const selectedEmployeeData = employees.find((employee) => employee.uuid === selectedUuid);
                        setSelectedEmployee(selectedUuid);
                        setSelectedPid(selectedEmployeeData?.p_id || null); // Dynamically set the pid
                        console.log("Selected Employee:", selectedEmployeeData);
                    }}
                    value={selectedEmployee || ""}
                    className="block px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm w-96 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                    <option value="">Select Employee</option>
                    {employees
                        .filter((employee) => {
                            // Filter employees based on activeStatus
                            if (activeStatus === "active") return employee?.active == 1 && employee.entity === 'erp360';
                            if (activeStatus === "inactive") return employee?.active == 0 && employee.entity === 'erp360';
                            return employee.entity === 'erp360'; // Show all employees for "all"
                        })
                        .map((employee) => (
                            <option
                                key={employee.uuid}
                                value={employee.uuid}
                                data-uuid={employee.uuid}
                                data-pid={employee.pid}
                                data-p_name={employee.p_name}
                            >
                                {employee.name} 
                            </option>
                        ))}
                </select>
                {/* <h3 className="pt-0 text-xs text-gray-500">{selectedEmployee || ""}</h3> */}
            </section>

            <section className="px-4 py-6 sm:px-6 lg:px-8">
                {/* Display Total Hours above the calendar */}
                <div className="mb-4">
                    <p className="text-sm font-semibold">
                        Total Hours: {totalHours} hours
                    </p>
                    {/* Debug: Show invoice status */}
                    {invoices.length > 0 && (
                        <div className="mt-2 p-2 bg-yellow-100 border border-yellow-400 rounded">
                            <p className="text-sm text-yellow-800 font-semibold">
                                ⚠️ Invoice Status: {invoices.length} invoice(s) found for current period
                            </p>
                            <ul className="text-xs text-yellow-700 mt-1">
                                {invoices.map((invoice, idx) => (
                                    <li key={idx}>
                                        Type: {invoice.invoice_type}, Month: {invoice.invoice_month}, Year: {invoice.invoice_year}
                                        {(invoice as any).invoice_no && `, Invoice #: ${(invoice as any).invoice_no}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {selectedEmployee && selectedPid && invoices.length === 0 && (
                        <p className="text-xs text-green-600 mt-1">
                            ✅ No invoices found - calendar is editable
                        </p>
                    )}
                </div>

                <div
                    style={{
                        transform: "scale(1.5)", // Adjusted scale to make the calendar fit better
                        transformOrigin: "top left",
                        overflow: "auto", // Enable scrolling
                        maxHeight: "500px", // Set a maximum height for the scrollable area
                        marginBottom: "40px", // Increased space below the calendar
                    }}
                >
                    <style>{`
                        .react-calendar__tile:disabled {
                            background-color: #f3f4f6 !important;
                            color: #d1d5db !important;
                            cursor: not-allowed !important;
                        }
                        .react-calendar__tile:disabled:hover {
                            background-color: #f3f4f6 !important;
                        }
                        .react-calendar__month-view__days__day--neighboringMonth {
                            visibility: hidden !important;
                        }
                    `}</style>
                    <Calendar
                        onChange={(newDate) => {
                            setDate(newDate as Date);
                            setFocusedDate(newDate as Date);
                        }}
                        onActiveStartDateChange={(e) => {
                            handleNavigation(e);
                        }}
                        onViewChange={() => {}}
                        onClickDay={(date) => {
                            setFocusedDate(date);
                            handleDateClick(date);
                        }}
                        value={date}
                        key={tileRefreshTrigger}
                        tileContent={renderTileContent}
                        tileDisabled={renderTileDisabled}
                        className={"rounded-lg border border-gray-0 shadow-sm"}
                    />
                </div>

                {/* Add more space below the calendar */}
                <div style={{ marginTop: "40px" }}></div>
            </section>
        </main>
    );
}
